import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Paperclip, Save, Send, Trash2, X } from 'lucide-react';
import EmailBody from '../components/EmailBody';
import { apiMessage } from './api';
import { fmtSize } from './Attachments';

/**
 * Writing a reply, reply-all, forward or new message, as a draft in the
 * mailbox's Drafts folder (so it is there in Outlook too, and anyone with mail
 * rights can pick it up).
 *
 * What a person writes is plain text. A reply or forward's quote of the
 * original is the mail system's own and is never edited here; it is shown in
 * the sandboxed body viewer, since it is the sender's HTML.
 *
 * **Nothing is saved on its own** (2026-09-29): changes are kept here until
 * Save draft or Send. Closing, or leaving for another message, a folder or a
 * new message, with unsaved changes asks "Save draft / Don't save / Keep
 * writing" (the page calls `confirmLeave`), and the browser warns before the
 * tab closes. A new message is not a draft until it is saved. A reply or
 * forward is: Exchange builds its recipients, subject and quote by making one
 * when Reply is clicked, so that draft is deleted if the person does not save
 * it (Don't save, or closing without typing). A draft opened from Drafts is
 * left as it was last saved.
 *
 * Sending goes at most once: the service claims it first, and a second click,
 * or a send that timed out, is refused rather than sent again.
 *
 * **Attachments go onto the draft as they are added** (Attach, or files dropped
 * on the composer), one at a time, so adding one is a save: a new message
 * becomes a draft to hold it, and a reply is kept rather than thrown away on
 * close. A forward's own attachments are listed and can be removed like any
 * other. Images drawn in the quoted body (a signature logo) are not listed.
 * The service limits a file to 15 MB and a draft's files together to 25 MB.
 */

// The service's limits (mailbox_core.compose), checked here first so a file
// too big is refused before it is uploaded.
export const MAX_FILE = 15 * 1024 * 1024;
export const MAX_FILES_TOTAL = 25 * 1024 * 1024;

const KIND_LABEL = {
  reply: 'Reply',
  reply_all: 'Reply all',
  forward: 'Forward',
  new: 'New message',
};

// Recipients are pills: { name, address }. What is saved and sent is the
// addresses alone.
const EMAIL = /[^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+/g;
const ONE_EMAIL = /^[^\s<>,;"]+@[^\s<>,;"]+\.[^\s<>,;"]+$/;
export const parseAddresses = (value) => (value || '').match(EMAIL) || [];

/** One typed entry as a recipient: "pat@x.com", "<pat@x.com>" or "Pat Jones <pat@x.com>". */
export const parseEntry = (raw) => {
  const text = (raw || '').trim();
  const named = /^"?([^"<]*?)"?\s*<([^<>\s]+)>$/.exec(text);
  if (named && ONE_EMAIL.test(named[2])) return { name: named[1].trim(), address: named[2] };
  return ONE_EMAIL.test(text) ? { name: '', address: text } : null;
};

/**
 * Typed or pasted text as recipients: the entries that are addresses, and what
 * is left over (not an address), joined back for the person to fix.
 */
export const takeEntries = (text) => {
  const entries = (text || '')
    .split(/[,;\n]+/)
    .map((e) => e.trim())
    .filter(Boolean);
  const found = [];
  const rest = [];
  entries.forEach((e) => {
    const r = parseEntry(e);
    if (r) found.push(r);
    else rest.push(e);
  });
  return { found, rest: rest.join(', ') };
};

/** Recipients added to a list, an address once (case-insensitive). */
export const addRecipients = (list, more) => {
  const seen = new Set(list.map((r) => r.address.toLowerCase()));
  const out = [...list];
  more.forEach((r) => {
    const key = r.address.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(r);
    }
  });
  return out;
};

const SUGGEST_AFTER_MS = 250;

// A search input drawn as a plain text field: no clear button, no search styling.
const SEARCH_PLAIN =
  'appearance-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none';

// Keeps the browser's own autofill and password managers off these fields: an
// address-like box otherwise draws saved logins over our suggestions. The part
// that works in Chrome and Edge is type="search": their password manager ignores
// the autocomplete attribute and guesses "username field" from the page, but
// never offers logins on a search box (checked 2026-09-29, after the attributes
// alone did not stop it). The rest are other managers' own opt-outs (LastPass,
// 1Password, Bitwarden, Dashlane), and a name free of "email", "user" and
// "login", which their heuristics key on. The search box's own clear button is
// hidden (SEARCH_PLAIN).
//
// Chrome's form history (what was typed into a field of the same name before)
// is a third list again: autocomplete="off" turns it off, and the name carries a
// token new on every page load, so no saved entry ever matches the field.
const PAGE_TOKEN = Math.random().toString(36).slice(2, 8);
export const noAutofill = (name) => ({
  type: 'search',
  name: `mbx-compose-${name}-${PAGE_TOKEN}`,
  autoComplete: 'off',
  autoCorrect: 'off',
  autoCapitalize: 'off',
  spellCheck: false,
  'data-lpignore': 'true',
  'data-1p-ignore': 'true',
  'data-bwignore': 'true',
  'data-form-type': 'other',
});

/**
 * To / Cc / Bcc as pills, with suggestions for what is being typed (past
 * senders, DelAir employees, ServiceTitan customers).
 *
 * A pill is made by picking a suggestion, or by finishing an address with
 * Enter, Tab, a comma or a semicolon, or by leaving the field; a pasted list
 * becomes pills at once. Each pill has a ×, and Backspace in an empty field
 * removes the last one. Text that is not an address stays in the field, drawn
 * red once the field is left, and is reported to the composer (`onPending`)
 * so a send can refuse it rather than drop it.
 */
const AddressField = ({ api, label, value, onChange, onPending, placeholder, right }) => {
  const [text, setText] = useState('');
  const [left, setLeft] = useState(false); // the field was left with text in it
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const timer = useRef(null);
  const asked = useRef(0);
  const input = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const setPending = (t) => {
    setText(t);
    if (onPending) onPending(t.trim());
  };

  const ask = (typed) => {
    clearTimeout(timer.current);
    const token = typed.trim();
    if (!api.suggestAddresses || token.length < 2 || /[<>]/.test(token)) {
      setItems([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      const mine = ++asked.current;
      try {
        const found = await api.suggestAddresses(token);
        if (mine !== asked.current) return;
        setItems(found);
        setActive(0);
        setOpen(true);
      } catch {
        if (mine === asked.current) setItems([]); // suggestions are extra; typing still works
      }
    }, SUGGEST_AFTER_MS);
  };

  // Turn whatever is typed into pills; keep what is not an address as text.
  const commit = (typed) => {
    const { found, rest } = takeEntries(typed);
    if (found.length) onChange(addRecipients(value, found));
    setPending(rest);
    return rest;
  };

  const pick = (s) => {
    onChange(addRecipients(value, [{ name: s.name || '', address: s.address }]));
    setPending('');
    setItems([]);
    setOpen(false);
    input.current?.focus();
  };

  const remove = (address) => {
    onChange(value.filter((r) => r.address !== address));
    input.current?.focus();
  };

  const onKeyDown = (e) => {
    if (open && items.length) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive((i) => (i + 1) % items.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((i) => (i - 1 + items.length) % items.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pick(items[active]);
        return;
      }
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
    }
    if ((e.key === 'Enter' || e.key === 'Tab' || e.key === ',' || e.key === ';') && text.trim()) {
      const rest = commit(text);
      // Tab moves on unless something could not be made a pill.
      if (e.key !== 'Tab' || rest) e.preventDefault();
      setOpen(false);
    } else if (e.key === 'Backspace' && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  const onType = (typed) => {
    setLeft(false);
    if (/[,;\n]/.test(typed)) {
      // A separator typed or a list pasted: everything before the last one
      // becomes pills, the last piece stays to be typed on.
      const cut = Math.max(typed.lastIndexOf(','), typed.lastIndexOf(';'), typed.lastIndexOf('\n'));
      const rest = commit(typed.slice(0, cut));
      const tail = typed.slice(cut + 1).trimStart();
      setPending(rest ? `${rest}, ${tail}` : tail);
      ask(tail);
      return;
    }
    setPending(typed);
    ask(typed);
  };

  const bad = left && text.trim() !== '';

  return (
    <div className="relative">
      <label className="flex items-start gap-2 border-b border-gray-100 py-1 text-sm">
        <span className="w-14 shrink-0 pt-1 text-gray-500">{label}</span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
          {value.map((r) => (
            <span
              key={r.address}
              title={r.name ? `${r.name} <${r.address}>` : r.address}
              className="inline-flex max-w-full items-center gap-0.5 rounded-full bg-gray-100 py-0.5 pl-2 pr-0.5 text-sm text-gray-800 ring-1 ring-inset ring-gray-200"
            >
              <span className="truncate">{r.name || r.address}</span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => remove(r.address)}
                aria-label={`Remove ${r.address}`}
                className="rounded-full px-1 leading-none text-gray-500 hover:bg-gray-200 hover:text-gray-800"
              >
                ×
              </button>
            </span>
          ))}
          <input
            {...noAutofill(label.toLowerCase())}
            ref={input}
            role="combobox"
            aria-expanded={open && items.length > 0}
            aria-invalid={bad || undefined}
            value={text}
            onChange={(e) => onType(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={() => {
              setOpen(false);
              if (text.trim()) commit(text);
              setLeft(true);
            }}
            placeholder={value.length ? '' : placeholder}
            aria-label={label}
            aria-autocomplete="list"
            className={`min-w-[8rem] flex-1 bg-transparent py-0.5 placeholder-gray-400 focus:outline-none ${
              bad ? 'text-red-600' : 'text-gray-900'
            } ${SEARCH_PLAIN}`}
          />
        </div>
        {right && <span className="pt-1">{right}</span>}
      </label>
      {open && items.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-16 right-0 z-30 mt-0.5 max-h-64 overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
        >
          {items.map((s, i) => (
            <li
              key={s.address}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the field
                pick(s);
              }}
              onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3 py-1.5 ${i === active ? 'bg-blue-50' : ''}`}
            >
              <div className="truncate text-gray-900">
                {s.name ? `${s.name} ` : ''}
                <span className="text-gray-500">{s.name ? `<${s.address}>` : s.address}</span>
              </div>
              {s.detail && <div className="truncate text-xs text-gray-400">{s.detail}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, right }) => (
  <label className="flex items-center gap-2 border-b border-gray-100 py-1.5 text-sm">
    <span className="w-14 shrink-0 text-gray-500">{label}</span>
    <input
      {...noAutofill(label.toLowerCase())}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`min-w-0 flex-1 bg-transparent py-0.5 text-gray-900 placeholder-gray-400 focus:outline-none ${SEARCH_PLAIN}`}
    />
    {right}
  </label>
);

const Composer = forwardRef(({ api, start, onClose, onCreated }, ref) => {
  // `start`: { draftId, draft? } for a draft, or { kind: 'new' }. The page makes
  // a reply or forward draft itself, once per click, and hands it over:
  // creating one here, in an effect, made two in development (StrictMode runs
  // effects twice). A new message is created here, but only on the first save
  // of something typed, so an empty one is never a draft.
  const [draft, setDraft] = useState(null);
  const [form, setForm] = useState(null);
  const [showCopies, setShowCopies] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [state, setState] = useState({ busy: 'loading', error: null, savedAt: null });
  const dirty = useRef({});
  // "Save this message as a draft?", and what to do once it is answered.
  const [asking, setAsking] = useState(null);
  // Saved at least once here: a draft that is the person's to keep.
  const savedOnce = useRef(false);
  // A draft this composer (or the page, for a reply) created, not one opened
  // from Drafts: the one "Don't save" deletes if it was never saved.
  const createdHere = useRef(Boolean(start.draft));
  // The draft as it is now (timers and the unmount cleanup read this, not state).
  const draftRef = useRef(null);
  // A new message has no draft until something is typed; this is its creation,
  // shared so two saves close together cannot make two drafts.
  const creating = useRef(null);
  // Whether anything was typed at all: a reply or forward made here and closed
  // untouched is thrown away rather than left in Drafts.
  const touched = useRef(false);
  // The form as of the last change, for a send that runs before a re-render,
  // and what each address field holds that is not (yet) a pill.
  const formRef = useRef(null);
  const pending = useRef({});
  // The draft's files, and those still going up (or refused): { key, name, size, error }.
  const [files, setFiles] = useState([]);
  const [uploads, setUploads] = useState([]);
  const fileInput = useRef(null);
  const [dropping, setDropping] = useState(false);

  const adopt = (d) => {
    setDraft(d);
    draftRef.current = d;
  };

  const fill = (d) => {
    adopt(d);
    const next = {
      to: d.to || [],
      cc: d.cc || [],
      bcc: d.bcc || [],
      subject: d.subject || '',
      text: d.text || '',
    };
    setForm(next);
    formRef.current = next;
    setShowCopies(Boolean((d.cc && d.cc.length) || (d.bcc && d.bcc.length)));
  };

  // The draft to save into, made on first need for a new message.
  const ensureDraft = () => {
    if (draftRef.current) return Promise.resolve(draftRef.current);
    if (!creating.current) {
      creating.current = api.createDraft('new').then((d) => {
        adopt(d);
        createdHere.current = true;
        if (onCreated) onCreated(d); // Drafts has one more message
        return d;
      });
      creating.current.catch(() => {
        creating.current = null; // let the next save try again
      });
    }
    return creating.current;
  };

  useEffect(() => {
    let live = true;
    (async () => {
      if (!start.draft && !start.draftId) {
        // A new message: an empty form, and nothing in Drafts yet.
        const blank = { to: [], cc: [], bcc: [], subject: '', text: '' };
        setForm(blank);
        formRef.current = blank;
        setState({ busy: null, error: null, savedAt: null });
        return;
      }
      try {
        const d = start.draft || (await api.getDraft(start.draftId));
        if (live) {
          fill(d);
          setState({ busy: null, error: null, savedAt: null });
        }
        if (d.has_attachments && api.listDraftAttachments) {
          try {
            const list = await api.listDraftAttachments(d.id);
            if (live) setFiles(list);
          } catch (err) {
            const why = apiMessage(err, 'Could not list the attachments.');
            if (live) setState((s) => ({ ...s, error: why }));
          }
        }
      } catch (err) {
        if (live) setState({ busy: null, error: apiMessage(err, 'Could not open the draft.') });
      }
    })();
    return () => {
      live = false;
    };
  }, []); // eslint-disable-line

  const unsaved = () => Object.keys(dirty.current).length > 0;

  // The browser's own warning before a tab with an unsaved message closes.
  useEffect(() => {
    const warn = (e) => {
      if (!unsaved()) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []); // eslint-disable-line

  // Save what changed since the last save. Returns false if it could not.
  const save = async () => {
    const changes = dirty.current;
    if (!Object.keys(changes).length) return true;
    dirty.current = {};
    setState((s) => ({ ...s, busy: s.busy || 'saving', error: null }));
    try {
      const d = await ensureDraft();
      await api.saveDraft(d.id, changes);
      savedOnce.current = true;
      setState((s) => ({ ...s, busy: s.busy === 'saving' ? null : s.busy, savedAt: new Date() }));
      return true;
    } catch (err) {
      dirty.current = { ...changes, ...dirty.current }; // try these again next time
      setState((s) => ({
        ...s,
        busy: s.busy === 'saving' ? null : s.busy,
        error: apiMessage(err, 'Could not save the draft.'),
      }));
      return false;
    }
  };

  const change = (key, value) => {
    touched.current = true;
    setForm((f) => ({ ...f, [key]: value }));
    formRef.current = { ...formRef.current, [key]: value };
    dirty.current[key] = ['to', 'cc', 'bcc'].includes(key) ? value.map((r) => r.address) : value;
  };

  // Attach files one after another, in the order given. Each is a save.
  const addFiles = async (picked) => {
    const list = [...(picked || [])];
    if (!list.length) return;
    touched.current = true;
    let total = files.reduce((n, a) => n + (a.size || 0), 0);
    for (const file of list) {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2, 8)}`;
      let error = null;
      if (file.size === 0) error = 'empty file';
      else if (file.size > MAX_FILE) error = `over ${MAX_FILE / 1048576} MB`;
      else if (total + file.size > MAX_FILES_TOTAL) {
        error = `attachments over ${MAX_FILES_TOTAL / 1048576} MB together`;
      }
      setUploads((u) => [...u, { key, name: file.name, size: file.size, error }]);
      if (error) continue; // eslint-disable-line no-continue
      try {
        const d = await ensureDraft(); // eslint-disable-line no-await-in-loop
        const now = await api.addDraftAttachment(d.id, file); // eslint-disable-line no-await-in-loop
        savedOnce.current = true;
        total += file.size;
        setFiles(now);
        setUploads((u) => u.filter((x) => x.key !== key));
      } catch (err) {
        const why = apiMessage(err, 'could not attach');
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: why } : x)));
      }
    }
  };

  const removeFile = async (a) => {
    try {
      await api.removeDraftAttachment(draftRef.current.id, a.id);
      setFiles((list) => list.filter((x) => x.id !== a.id));
    } catch (err) {
      setState((s) => ({ ...s, error: apiMessage(err, 'Could not remove the attachment.') }));
    }
  };

  const attaching = uploads.some((u) => !u.error);
  const shownFiles = files.filter((a) => !a.inline);

  // Files dragged in from the computer. A message row dragged from the list is
  // not a file, so it is left to the folder tree.
  const hasFiles = (e) => [...(e.dataTransfer?.types || [])].includes('Files');
  const dropHandlers = api.addDraftAttachment
    ? {
        onDragOver: (e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
          setDropping(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false);
        },
        onDrop: (e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          setDropping(false);
          addFiles(e.dataTransfer.files);
        },
      }
    : {};

  const send = async () => {
    // Read from the ref: leaving an address field (the click on Send) turns
    // what was typed into pills just before this runs.
    const f = formRef.current || form;
    const leftover = Object.values(pending.current).find(Boolean);
    if (leftover) {
      setState((s) => ({ ...s, error: `Not an email address: ${leftover}` }));
      return;
    }
    if (![...f.to, ...f.cc, ...f.bcc].length) {
      setState((s) => ({ ...s, error: 'Add at least one recipient.' }));
      return;
    }
    if (attaching) {
      setState((s) => ({ ...s, error: 'Wait for the attachments to finish.' }));
      return;
    }
    setState((s) => ({ ...s, busy: 'sending', error: null }));
    if (!(await save())) {
      setState((s) => ({ ...s, busy: null }));
      return;
    }
    try {
      await api.sendDraft(draftRef.current.id);
      dirty.current = {};
      onClose({ sent: true });
    } catch (err) {
      setState((s) => ({ ...s, busy: null, error: apiMessage(err, 'Could not send.') }));
    }
  };

  const discard = async () => {
    if (!draftRef.current && !creating.current) {
      // Nothing was ever saved: nothing to delete.
      dirty.current = {};
      onClose({});
      return;
    }
    if (!window.confirm('Discard this draft? It is deleted from Drafts.')) return;
    dirty.current = {};
    setState((s) => ({ ...s, busy: 'discarding', error: null }));
    try {
      const d = await ensureDraft();
      await api.discardDraft(d.id);
      onClose({ discarded: true });
    } catch (err) {
      setState((s) => ({ ...s, busy: null, error: apiMessage(err, 'Could not discard the draft.') }));
    }
  };

  const exit = (result, next) => {
    onClose(result);
    if (next) next();
  };

  // Leave without saving what has not been saved. A draft made here and never
  // saved is deleted; one saved earlier, or opened from Drafts, stays as it was.
  const leaveUnsaved = async (next) => {
    dirty.current = {};
    setAsking(null);
    if (draftRef.current && createdHere.current && !savedOnce.current) {
      try {
        await api.discardDraft(draftRef.current.id);
        exit({ dropped: true }, next);
        return;
      } catch {
        /* could not throw it away: it stays in Drafts */
      }
    }
    exit(savedOnce.current ? { saved: true } : {}, next);
  };

  const leaveSaved = async (next) => {
    setAsking(null);
    if (await save()) exit({ saved: true }, next);
  };

  // Ask before leaving with unsaved changes; with none, just go.
  const confirmLeave = (next) => {
    if (unsaved()) setAsking({ next });
    else leaveUnsaved(next);
  };
  useImperativeHandle(ref, () => ({ confirmLeave }));

  const close = () => confirmLeave(null);

  const busy = state.busy;
  const title = KIND_LABEL[draft?.kind || start.kind] || 'Draft';

  return (
    <div
      className={`rounded-md border ${dropping ? 'border-blue-400 ring-2 ring-blue-200' : 'border-gray-200'}`}
      {...dropHandlers}
    >
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
        <span className="flex-1 text-sm font-semibold text-gray-800">{title}</span>
        <span className="text-xs text-gray-500">
          {busy === 'saving' && 'Saving…'}
          {!busy && unsaved() && 'Not saved'}
          {!busy &&
            !unsaved() &&
            state.savedAt &&
            `Saved ${state.savedAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
          {draft?.created_by && ` · started by ${draft.created_by}`}
        </span>
        <button
          type="button"
          onClick={close}
          className="rounded p-1 text-gray-500 hover:bg-gray-200"
          aria-label="Close"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {busy === 'loading' && <div className="p-3 text-sm text-gray-500">Opening the draft…</div>}
      {!form && state.error && <div className="p-3 text-sm text-red-700">{state.error}</div>}
      {form && (
        <div className="px-3 pb-3">
          <AddressField
            api={api}
            label="To"
            value={form.to}
            onChange={(v) => change('to', v)}
            onPending={(t) => {
              pending.current.to = t;
            }}
            placeholder="Name or address"
            right={
              !showCopies && (
                <button
                  type="button"
                  onClick={() => setShowCopies(true)}
                  className="shrink-0 text-xs text-blue-700 hover:underline"
                >
                  Cc / Bcc
                </button>
              )
            }
          />
          {showCopies && (
            <>
              <AddressField
                api={api}
                label="Cc"
                value={form.cc}
                onChange={(v) => change('cc', v)}
                onPending={(t) => {
                  pending.current.cc = t;
                }}
              />
              <AddressField
                api={api}
                label="Bcc"
                value={form.bcc}
                onChange={(v) => change('bcc', v)}
                onPending={(t) => {
                  pending.current.bcc = t;
                }}
              />
            </>
          )}
          <Field label="Subject" value={form.subject} onChange={(v) => change('subject', v)} />
          <textarea
            value={form.text}
            onChange={(e) => change('text', e.target.value)}
            rows={10}
            autoFocus
            placeholder="Write your message"
            aria-label="Message"
            className="mt-2 w-full resize-y rounded border border-gray-300 p-2 text-base leading-relaxed text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {(shownFiles.length > 0 || uploads.length > 0) && (
            <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Attachments">
              {shownFiles.map((a) => (
                <li
                  key={a.id}
                  className="inline-flex max-w-full items-center gap-1 rounded-md border border-gray-200 bg-gray-50 py-0.5 pl-2 pr-0.5 text-sm text-gray-800"
                >
                  <Paperclip className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <span className="truncate" title={a.name}>
                    {a.name}
                  </span>
                  {a.size > 0 && <span className="shrink-0 text-xs text-gray-500">{fmtSize(a.size)}</span>}
                  <button
                    type="button"
                    onClick={() => removeFile(a)}
                    disabled={Boolean(busy)}
                    aria-label={`Remove ${a.name}`}
                    className="rounded px-1 leading-none text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
                  >
                    ×
                  </button>
                </li>
              ))}
              {uploads.map((u) => (
                <li
                  key={u.key}
                  className={`inline-flex max-w-full items-center gap-1 rounded-md border py-0.5 pl-2 pr-0.5 text-sm ${
                    u.error ? 'border-red-200 bg-red-50 text-red-800' : 'border-gray-200 bg-white text-gray-500'
                  }`}
                >
                  <Paperclip className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate" title={u.name}>
                    {u.name}
                  </span>
                  <span className="shrink-0 text-xs">{u.error ? `Not attached: ${u.error}` : 'Attaching…'}</span>
                  {u.error && (
                    <button
                      type="button"
                      onClick={() => setUploads((list) => list.filter((x) => x.key !== u.key))}
                      aria-label={`Dismiss ${u.name}`}
                      className="rounded px-1 leading-none hover:bg-red-100"
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {dropping && <div className="mt-1 text-xs text-blue-700">Drop to attach.</div>}
          {draft?.quoted && (
            <div className="mt-2">
              <button
                type="button"
                onClick={() => setShowQuote((q) => !q)}
                className="inline-flex items-center text-xs text-gray-600 hover:text-gray-900"
              >
                {showQuote ? (
                  <ChevronDown className="mr-0.5 h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="mr-0.5 h-3.5 w-3.5" />
                )}
                {draft?.kind === 'new' ? 'Rest of the message' : 'Quoted original'}
              </button>
              {showQuote && (
                <div className="mt-1">
                  <EmailBody
                    body={draft?.quoted}
                    textClassName="whitespace-pre-wrap break-words rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700"
                    frameClassName="w-full h-[360px] rounded border border-gray-200 bg-white"
                    maxChars={null}
                  />
                </div>
              )}
            </div>
          )}
          {state.error && <div className="mt-2 text-sm text-red-700">{state.error}</div>}
          {asking && (
            <div
              role="alertdialog"
              aria-label="Save this message as a draft?"
              className="mt-3 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              <span className="flex-1">Save this message as a draft?</span>
              <button
                type="button"
                onClick={() => leaveSaved(asking.next)}
                disabled={Boolean(busy)}
                className="rounded-md bg-blue-600 px-2.5 py-1 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Save draft
              </button>
              <button
                type="button"
                onClick={() => leaveUnsaved(asking.next)}
                disabled={Boolean(busy)}
                className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Don't save
              </button>
              <button
                type="button"
                onClick={() => setAsking(null)}
                className="rounded-md px-2.5 py-1 text-sm text-gray-700 hover:bg-amber-100"
              >
                Keep writing
              </button>
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={send}
              disabled={Boolean(busy) || attaching}
              className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {busy === 'sending' ? 'Sending…' : attaching ? 'Attaching…' : 'Send'}
            </button>
            <button
              type="button"
              onClick={save}
              disabled={Boolean(busy) || !unsaved()}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Save className="h-4 w-4" />
              {busy === 'saving' ? 'Saving…' : 'Save draft'}
            </button>
            {api.addDraftAttachment && (
              <>
                <button
                  type="button"
                  onClick={() => fileInput.current?.click()}
                  disabled={Boolean(busy)}
                  className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  <Paperclip className="h-4 w-4" />
                  Attach
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  aria-label="Attach files"
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = ''; // the same file can be picked again
                  }}
                />
              </>
            )}
            <button
              type="button"
              onClick={discard}
              disabled={Boolean(busy)}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default Composer;
